import getReturnMethod, {
  DROP_OFF_RETURN_METHOD,
  EPARCEL,
  HUBBED,
  PICK_UP,
  RETURN_IN_STORE,
  ReturnMethod,
  SELF_RETURN,
} from '../../logic/GetReturnMethod';
import Method, {
  EparcelDropOffItem,
  ReturnInStoreItem,
  PickUpItem,
  SelfReturnItem,
} from './Method';
import { Settings } from '../../types/settings';
import { QuotesResponse } from '../../fetch/types';
import HubbedMethod from './Methods/HubbedMethod';
import DropOffMethod from './Methods/DropOffMethod';
import {
  Carrier,
  NINJA_VAN_CARRIER_TYPE,
  MORNING_EXPRESS_CARRIER_TYPE,
  PLAIN_LABEL_CARRIER_TYPE,
  SING_POST_CARRIER_TYPE,
  HUBBED_CARRIER_TYPE,
  EPARCEL_CARRIER_TYPE,
} from '../../types/carrier';
import { OrderReturnRequest } from '../../types/order';
import {
  formatCurrency,
  getCurrencyCode,
} from '../../components/util/Currency';

export const PO_BOX_ERROR =
  'The courier is unable to pick up from PO boxes or parcel lockers. Please update your address.';
export const PICKUP_ERROR =
  'The courier is unable to pick up from the current location. Please update your address.';
export const DROP_OFF_ERROR =
  'Drop off is not available for the current location. Please update your address.';

const AU_METHOD_DISPLAY_ORDER = {
  Hubbed: 0,
  Eparcel: 1,
  PickUp: 2,
};
const SEA_DISPLAY_ORDER = {
  DropOff: 0,
  PickUp: 1,
  InStore: 2,
  SelfReturn: 3,
};

export const RETURN_IN_STORE_CARRIER: Carrier = {
  carrier_type: PLAIN_LABEL_CARRIER_TYPE,
  carrier_name: 'Return in store',
  service_level: 'standard',
  dropoff: true,
};

export const SELF_RETURN_CARRIER: Carrier = {
  carrier_type: PLAIN_LABEL_CARRIER_TYPE,
  carrier_name: 'Self Return',
  service_level: 'standard',
  dropoff: true,
};

class ReturnMethodStore {
  private settings: Settings | null = null;

  private countryCode: string = 'AU';

  private method: ReturnMethod | null = null;

  private methods: Array<Method> = [];

  private returnDropoffCharge: string | undefined;

  private returnPickupCharge: string | undefined;

  private currencyCode: string | undefined;

  set(settings: Settings, countryCode: string = 'AU') {
    this.settings = {
      ...settings,
      dropoff_enabled: settings.dropoff_enabled,
      pickup_enabled: settings.pickup_enabled,
      // INTL-1449: TODO remove hubbed_enabled and eparcel_dropoff_enabled when enable_new_fields_for_au fully rolled out
      hubbed_enabled: settings.hubbed_enabled,
      eparcel_dropoff_enabled: settings.eparcel_dropoff_enabled,
    };
    this.countryCode = countryCode.toUpperCase();
    this.method = this.getAvailableMethods();
  }

  setReturnsRequest(
    returnsRequest: OrderReturnRequest,
    merchantCurrency: string
  ) {
    this.currencyCode = getCurrencyCode(returnsRequest, merchantCurrency);

    const dropoffCharge = returnsRequest.customer_return_dropoff_charge;
    if (dropoffCharge !== undefined || dropoffCharge != null) {
      this.returnDropoffCharge = formatCurrency(
        dropoffCharge,
        this.currencyCode
      );
    }

    const pickupCharge = returnsRequest.customer_return_pickup_charge;
    if (pickupCharge !== undefined || pickupCharge != null) {
      this.returnPickupCharge = formatCurrency(pickupCharge, this.currencyCode);
    }
  }

  setEparcel() {
    this.method = EPARCEL;
  }

  setPickUp() {
    this.method = PICK_UP;
  }

  setHubbed() {
    this.method = HUBBED;
  }

  setDropoff() {
    this.method = DROP_OFF_RETURN_METHOD;
  }

  setReturnInStore() {
    this.method = RETURN_IN_STORE;
  }

  setSelfReturn() {
    this.method = SELF_RETURN;
  }

  private buildeParcelMethod(recommendations: Carrier[]) {
    const eparcelIndex = recommendations.findIndex(
      rec => rec.dropoff && rec.carrier_type === 'Eparcel'
    );
    return new Method(
      EparcelDropOffItem,
      eparcelIndex !== -1 ? AU_METHOD_DISPLAY_ORDER.Eparcel : eparcelIndex,
      recommendations[eparcelIndex]
    );
  }

  private buildHubbedMethod(recommendations: Carrier[]) {
    const hubbedIndex = recommendations.findIndex(
      rec => rec.carrier_type === 'Hubbed'
    );

    const hubbedDropOff = new HubbedMethod(
      AU_METHOD_DISPLAY_ORDER.Hubbed,
      recommendations[hubbedIndex]
    );

    return hubbedDropOff;
  }

  private buildOldPickupMethod(
    recommendations: Carrier[],
    response: QuotesResponse
  ) {
    const pickUpIndex = recommendations.findIndex(rec => !rec.dropoff);

    const pickUp = new Method(
      PickUpItem,
      pickUpIndex !== -1 ? AU_METHOD_DISPLAY_ORDER.PickUp : pickUpIndex,
      recommendations[pickUpIndex]
    );

    pickUp.poBoxError = response.to ? response.to.po_box_or_locker : null;
    return pickUp;
  }

  private buildDropoffMethod() {
    // INTL-1449: TODO remove this filter for hubbed and eparcel when enable_new_fields_for_au fully rolled out
    const dropOffProviders = (this.settings?.dropoff_providers || []).filter(
      provider =>
        provider !== HUBBED_CARRIER_TYPE && provider !== EPARCEL_CARRIER_TYPE
    );

    if (dropOffProviders.length) {
      return new DropOffMethod(
        SEA_DISPLAY_ORDER.DropOff,
        dropOffProviders,
        this.returnDropoffCharge,
        this.currencyCode
      );
    }
    return null;
  }

  private buildPickUpMethod(recommendations: Carrier[]) {
    const pickupProviders = this.settings?.pickup_providers || [];

    if (pickupProviders.length) {
      const firstPickupCarrier = recommendations.find(
        rec =>
          rec.carrier_type.toLowerCase() === pickupProviders[0].toLowerCase()
      );

      return new Method(
        PickUpItem,
        firstPickupCarrier ? SEA_DISPLAY_ORDER.PickUp : -1,
        firstPickupCarrier || null,
        this.returnPickupCharge,
        this.currencyCode
      );
    }

    return null;
  }

  private buildReturnInStoreMethod() {
    if (this.settings?.return_in_store) {
      return new Method(
        {
          ...ReturnInStoreItem,
          description: `Return your item in store during store hours at any ${this.settings.returns_store_name} store.`,
        },
        SEA_DISPLAY_ORDER.InStore,
        RETURN_IN_STORE_CARRIER,
        this.returnPickupCharge,
        this.currencyCode
      );
    }

    return null;
  }

  private buildSelfReturnMethod() {
    if (this.settings?.self_returns_enabled) {
      return new Method(
        SelfReturnItem,
        SEA_DISPLAY_ORDER.SelfReturn,
        SELF_RETURN_CARRIER,
        this.returnPickupCharge,
        this.currencyCode
      );
    }

    return null;
  }

  setRecommendation(response: QuotesResponse) {
    this.methods = [];

    if (!response) return;
    const { recommendations } = response.quotes;

    if (this.settings?.enable_new_fields_for_au || this.countryCode !== 'AU') {
      const eParcelMethod = this.buildeParcelMethod(recommendations);
      if (this.countryCode === 'AU' && eParcelMethod) {
        this.methods.push(eParcelMethod);
      }

      const hubbedMethod = this.buildHubbedMethod(recommendations);
      if (this.settings?.hubbed_enabled && hubbedMethod) {
        this.methods.push(hubbedMethod);
      }

      const dropOffMethod = this.buildDropoffMethod();
      if (dropOffMethod) {
        this.methods.push(dropOffMethod);
      }

      const pickUpMethod = this.buildPickUpMethod(recommendations);
      if (pickUpMethod) {
        this.methods.push(pickUpMethod);
      }

      const returnInStoreMethod = this.buildReturnInStoreMethod();
      if (returnInStoreMethod) {
        this.methods.push(returnInStoreMethod);
      }

      const selfReturnMethod = this.buildSelfReturnMethod();
      if (selfReturnMethod) {
        this.methods.push(selfReturnMethod);
      }
    } else {
      const eParcelMethod = this.buildeParcelMethod(recommendations);
      if (this.countryCode === 'AU' && eParcelMethod) {
        this.methods.push(eParcelMethod);
      }

      const hubbedMethod = this.buildHubbedMethod(recommendations);
      if (this.settings?.hubbed_enabled && hubbedMethod) {
        this.methods.push(hubbedMethod);
      }

      const pickUpMethod = this.buildOldPickupMethod(recommendations, response);
      if (pickUpMethod) {
        this.methods.push(pickUpMethod);
      }
    }

    this.methods.sort((a, b) => a.index - b.index);
  }

  getCountryCode() {
    return this.countryCode;
  }

  getMethods() {
    return this.methods;
  }

  getAvailableMethods() {
    return getReturnMethod(this.settings!);
  }

  get() {
    return this.method;
  }

  getMethod() {
    if (this.method === HUBBED) {
      return this.getHubbedMethod()!;
    }
    if (this.method === EPARCEL) {
      return this.getEparcelMethod()!;
    }
    if (this.method === DROP_OFF_RETURN_METHOD) {
      return this.getDropOffMethod()!;
    }
    if (this.method === RETURN_IN_STORE) {
      return this.getReturnInStoreMethod()!;
    }
    if (this.method === SELF_RETURN) {
      return this.getSelfReturnMethod()!;
    }
    return this.getPickUpMethod()!;
  }

  getPickUpName() {
    return this.getPickUpMethod()!.getName()!;
  }

  getHubbedMethod() {
    return this.getMethods().find(m => m.isHubbed()) as HubbedMethod;
  }

  getEparcelMethod() {
    return this.getMethods().find(m => m.isEparcel());
  }

  getPickUpMethod() {
    return this.getMethods().find(m => !m.isDropOff());
  }

  getDropOffMethod() {
    return this.getMethods().find(m => m.isDropOff()) as DropOffMethod;
  }

  getReturnInStoreMethod() {
    return this.getMethods().find(m => m.isReturnInStore()) as Method;
  }

  getSelfReturnMethod() {
    return this.getMethods().find(m => m.isSelfReturn()) as Method;
  }

  getError() {
    const availableMethods = this.getAvailableMethods();
    switch (availableMethods) {
      case EPARCEL: {
        const eparcelMethod = this.getEparcelMethod();
        return eparcelMethod && eparcelMethod.isAvailable()
          ? null
          : DROP_OFF_ERROR;
      }
      case HUBBED: {
        const hubbedMethod = this.getHubbedMethod();
        return hubbedMethod && hubbedMethod.isAvailable()
          ? null
          : DROP_OFF_ERROR;
      }
      case DROP_OFF_RETURN_METHOD: {
        const dropoffMethod = this.getDropOffMethod();
        return dropoffMethod && dropoffMethod.isAvailable()
          ? null
          : DROP_OFF_ERROR;
      }
      default: {
        const pickUp = this.getPickUpMethod();
        if (pickUp) {
          if (pickUp.poBoxError) {
            return PO_BOX_ERROR;
          }
          if (!pickUp.isAvailable()) {
            return PICKUP_ERROR;
          }
        }
        return null;
      }
    }
  }

  isSelectedMethodEparcel() {
    return this.method === EPARCEL;
  }

  isSelectedMethodHubbed() {
    return this.method === HUBBED;
  }

  isSelectedMethodPickUp() {
    return this.method === PICK_UP;
  }

  isSelectedMethodNinjaVanDropoff() {
    return (
      this.method === DROP_OFF_RETURN_METHOD &&
      this.getDropOffMethod().getDropoffCarrier() === NINJA_VAN_CARRIER_TYPE
    );
  }

  isSelectedMethodSingPost() {
    return (
      this.method === DROP_OFF_RETURN_METHOD &&
      this.getDropOffMethod().getDropoffCarrier() === SING_POST_CARRIER_TYPE
    );
  }

  isSelectedMethodMorningExpress() {
    return (
      this.method === DROP_OFF_RETURN_METHOD &&
      this.getDropOffMethod().getDropoffCarrier() ===
        MORNING_EXPRESS_CARRIER_TYPE
    );
  }

  isSelectedMethodReturnInStore() {
    return this.method === RETURN_IN_STORE;
  }

  isSelectedMethodSelfReturn() {
    return this.method === SELF_RETURN;
  }

  isEparcelSelectedAndUnAvailable() {
    const eparcelMethod = this.getEparcelMethod();
    return (
      this.isSelectedMethodEparcel() &&
      eparcelMethod &&
      !eparcelMethod.isAvailable()
    );
  }
}

export default ReturnMethodStore;
